<template>
  <v-container style="padding: 0; margin: 0">
    <treeselect
      v-model="store.selectedCityId"
      :options="treeCities"
      placeholder="Select Country / City"
      :default-expand-level="0"
      :multiple="true"
      :disable-branch-nodes="true"
      class="mb-4"
    />

    <div
      ref="mapContainer"
      style="
        height: 22.375rem;
        min-height: 22.375rem;
        border-radius: 10px;
        overflow: hidden;
        border: 2.7px solid #d6d6d6;
      "
    ></div>

    <v-slider
      v-if="selectedCoords.length"
      v-model="radius"
      label="Radius"
      min="15000"
      max="70000"
      step="100"
      class="mt-4 no-border-slider"
      color="#237276"
    />
  </v-container>
</template>

<script>
import Treeselect from "vue3-treeselect";
import "vue3-treeselect/dist/vue3-treeselect.css";
import apiClient from "@/core/buildRequest";
import { useSuperAddStore } from "@/store/superAddStore";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  components: { Treeselect },

  data() {
    return {
      store: useSuperAddStore(),
      treeCities: [],
      selectedCoords: [],
      selectedNames: [],
      selectedLatLngs: [],
      radius: 15000,
      map: null,
      markers: [],
      circles: [],
      activeIndex: null,
      geocodeCache: new Map(),
      lastGeocodeAt: 0,
    };
  },

  watch: {
    "store.selectedCityId"(newVal) {
      if (newVal) this.onCitySelected(newVal);
    },
    radius(val) {
      if (this.activeIndex !== null && this.circles[this.activeIndex]) {
        this.circles[this.activeIndex].setRadius(val);
        this.selectedLatLngs[this.activeIndex].radius = val;
        localStorage.setItem(
          "selectedLatLngs",
          JSON.stringify(this.selectedLatLngs)
        );
      }
    },
  },

  methods: {
    validate() {
      const hasValidLocation =
        Array.isArray(this.store.selectedCityId) &&
        this.store.selectedCityId.length > 0;

      if (!hasValidLocation) {
        return "Please select at least one location on the map.";
      }

      return null;
    },
    getData() {
      return {
        selectedNames: this.selectedNames,
        selectedLatLngs: this.selectedLatLngs,
      };
    },

    async fetchCountries() {
      try {
        const response = await apiClient.get("/super-ad/countries", {
          params: {
            platform: "tiktok",
            search: "",
          },
        });

        this.treeCities = response.data.data.map((country) => ({
          id: country.id,
          label: country.label,
          country_code: country.country_code,
          children: country.children
            ? country.children.map((city) => ({
                id: city.id,
                region_code: city.region_code,
                label: city.label,
                platform: city.platform,
              }))
            : [],
        }));
      } catch (error) {
        console.error("Failed to load countries", error);
      }
    },

    async fetchCoordsFromOpenStreetMap(cityName, countryName = "") {
      try {
        const query = `${cityName}, ${countryName}`;
        if (this.geocodeCache.has(query)) return this.geocodeCache.get(query);

        const waitTime = Math.max(0, 1100 - (Date.now() - this.lastGeocodeAt));
        if (waitTime) {
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
        this.lastGeocodeAt = Date.now();

        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(
            query
          )}`,
          { headers: { "Accept-Language": "en" } }
        );
        if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);
        const data = await response.json();
        const coords = data[0]
          ? { lat: Number(data[0].lat), lng: Number(data[0].lon) }
          : null;
        this.geocodeCache.set(query, coords);
        return coords;
      } catch (err) {
        console.error("OpenStreetMap geocoding failed", err);
        return null;
      }
    },

    async onCitySelected(cityIds) {
      this.markers.forEach((marker) => marker.remove());
      this.circles.forEach((circle) => circle.remove());
      this.markers = [];
      this.circles = [];
      this.selectedCoords = [];
      this.selectedNames = [];
      this.selectedLatLngs = [];

      for (let i = 0; i < cityIds.length; i++) {
        const id = cityIds[i];
        for (const country of this.treeCities) {
          const city = country.children.find((c) => c.id === id);
          if (city) {
            const coords = await this.fetchCoordsFromOpenStreetMap(
              city.label,
              country.label
            );
            if (coords) {
              const markerIndex = this.markers.length;
              const marker = L.marker([coords.lat, coords.lng], {
                draggable: true,
                icon: L.divIcon({
                  className: "location-marker",
                  html: '<span class="location-marker__dot"></span>',
                  iconSize: [22, 22],
                  iconAnchor: [11, 11],
                }),
              }).addTo(this.map);

              const circle = L.circle([coords.lat, coords.lng], {
                radius: this.radius,
                color: "#FF0000",
                weight: 0,
                fillColor: "#FF0000",
                fillOpacity: 0.2,
              }).addTo(this.map);

              marker.on("dragend", (event) => {
                const position = event.target.getLatLng();
                const newLatLng = {
                  lat: position.lat,
                  lng: position.lng,
                };
                circle.setLatLng(newLatLng);
                this.selectedCoords[markerIndex] = newLatLng;
                this.selectedLatLngs[markerIndex] = {
                  ...newLatLng,
                  radius: this.radius,
                };
                localStorage.setItem(
                  "selectedLatLngs",
                  JSON.stringify(this.selectedLatLngs)
                );
              });

              marker.on("click", () => {
                this.activeIndex = markerIndex;
              });

              this.markers.push(marker);
              this.circles.push(circle);
              this.selectedCoords.push(coords);
              this.activeIndex = this.selectedCoords.length - 1;

              let existingCountry = this.selectedNames.find(
                (c) => c.id === country.id
              );
              const cityData = {
                id: city.id,
                region_code: city.region_code,
                label: city.label,
                platform: city.platform,
              };

              if (existingCountry) {
                const alreadyExists = existingCountry.children.find(
                  (child) => child.id === city.id
                );
                if (!alreadyExists) {
                  existingCountry.children.push(cityData);
                }
              } else {
                this.selectedNames.push({
                  id: country.id,
                  label: country.label,
                  country_code: country.country_code,
                  children: [cityData],
                });
              }

              this.selectedLatLngs.push({
                lat: coords.lat,
                lng: coords.lng,
                radius: this.radius,
              });
            }
            break;
          }
        }
      }

      if (this.selectedCoords.length > 0) {
        this.map.fitBounds(
          this.selectedCoords.map((coords) => [coords.lat, coords.lng]),
          { padding: [40, 40], maxZoom: 7 }
        );
      }

      // Save to localStorage
      localStorage.setItem("selectedNames", JSON.stringify(this.selectedNames));
      localStorage.setItem(
        "selectedLatLngs",
        JSON.stringify(this.selectedLatLngs)
      );
    },
  },

  async mounted() {
    this.map = L.map(this.$refs.mapContainer).setView([26.8206, 30.8025], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(this.map);

    await this.fetchCountries();
    if (this.store.selectedCityId?.length) {
      await this.onCitySelected(this.store.selectedCityId);
    }
  },

  beforeUnmount() {
    this.map?.remove();
    this.map = null;
  },
};
</script>

<style scoped>
.no-border-slider {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

:deep(.location-marker) {
  background: transparent;
  border: 0;
}

:deep(.location-marker__dot) {
  display: block;
  width: 22px;
  height: 22px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #237276;
  box-shadow: 0 1px 5px rgb(0 0 0 / 45%);
}
</style>
